import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useMcAccessStatus } from '@/hooks/useMcAccessStatus';
import { useNotifications } from '@/contexts/NotificationContext';

// Persistent top banner shown on every page as long as the selected MC has
// access_status='permission_denied'. Replaces the blocking modal once dismissed
// or overridden — the user has been informed, this is the always-visible reminder.

export function AccessRevokedBanner() {
  const { t } = useTranslation(['optimization', 'common']);
  const {
    mcId,
    mcName,
    shouldShowPersistentBanner,
    isDeletionOverridden,
    daysUntilDeletion,
  } = useMcAccessStatus();
  const queryClient = useQueryClient();
  const { toast } = useNotifications();
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  if (!shouldShowPersistentBanner || !mcId) return null;

  const displayName = mcName ?? t('accessRevoked.banner.noAccessFallback');

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['mc-access-lifecycle', mcId] });
  };

  const handleKeep = async () => {
    setBusy(true);
    const { error } = await supabase.rpc('override_mc_deletion', { p_mc_id: mcId });
    setBusy(false);
    if (error) {
      toast({ title: t('accessRevoked.overlay.toast.errorTitle'), description: error.message, variant: 'destructive' });
      return;
    }
    toast({ tone: 'success', title: t('accessRevoked.overlay.toast.keptTitle') });
    invalidate();
  };

  const handleCancelOverride = async () => {
    setBusy(true);
    const { error } = await supabase.rpc('cancel_mc_deletion_override', { p_mc_id: mcId });
    setBusy(false);
    if (error) {
      toast({ title: t('accessRevoked.overlay.toast.errorTitle'), description: error.message, variant: 'destructive' });
      return;
    }
    toast({
      tone: 'success',
      title: t('accessRevoked.overlay.toast.keptTitle'),
      description: t('accessRevoked.overlay.toast.keptDesc'),
    });
    invalidate();
  };

  // G3-04: die Löschfrist steht VORNE im Kurzsatz, damit `truncate` sie nie
  // abschneidet. Sie ist die einzige Fristangabe vor einer irreversiblen
  // Löschung und gehört nie in den eingeklappten Teil.
  const shortText = isDeletionOverridden
    ? t('accessRevoked.banner.shortPaused')
    : daysUntilDeletion !== null
      ? t('accessRevoked.banner.shortDeletionIn', { count: daysUntilDeletion })
      : t('accessRevoked.banner.shortNoAccess');

  const headline = (
    <Trans
      i18nKey="accessRevoked.banner.noAccessTo"
      t={t}
      values={{ mcName: displayName }}
      components={{ bold: <strong /> }}
    />
  );

  const grantAccessLink = (extraClass: string) => (
    <a
      href="https://merchants.google.com"
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-1 rounded border border-destructive/40 bg-card/60 px-2 py-1 text-xs font-medium text-destructive-ink hover:bg-card ${extraClass}`}
    >
      {t('accessRevoked.banner.grantAccess')}
      <ExternalLink className="h-3 w-3" />
    </a>
  );

  return (
    // Stickiness lives on the wrapper in Layout, so this banner and the account-level
    // GoogleAuthRevokedBanner stack instead of overlapping each other at top: 0.
    <div
      role="alert"
      className="border-b border-destructive/30 bg-destructive/10 px-4 py-1 text-sm text-destructive-ink backdrop-blur sm:py-2 print:hidden"
    >
      {/*
        G3-04, Platzbilanz unter sm (Plan WP6 Task 9): Symbol 24 px, Kurzsatz als
        Schalter (min-h-11, truncate), genau EIN Knopf h-11 rechts (Behalten
        rund 76 px bzw. GMC öffnen). 53 px je Banner. Kurzsatz-Platz bei
        390/360/320 px: rund 230/200/160 px, "Löschung in 12 Tagen:" passt
        auch bei 320 px.
      */}
      <div data-testid="banner-mobile-row" className="flex min-h-11 items-center gap-2 sm:hidden">
        <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <button
          type="button"
          data-testid="banner-toggle"
          className="flex min-h-11 min-w-0 flex-1 items-center gap-1 text-left font-semibold"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          <span className="truncate">{shortText}</span>
          <ChevronDown
            className={`h-4 w-4 flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
          <span className="sr-only">
            {expanded ? t('common:banner.hideDetails') : t('common:banner.showDetails')}
          </span>
        </button>
        {isDeletionOverridden ? (
          <a
            href="https://merchants.google.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-11 flex-shrink-0 items-center gap-1 rounded border border-destructive/40 bg-card/60 px-3 text-xs font-medium text-destructive-ink hover:bg-card"
          >
            {t('accessRevoked.banner.openGmc')}
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        ) : (
          <Button
            type="button"
            size="sm"
            className="h-11 flex-shrink-0 px-3 text-xs"
            onClick={handleKeep}
            disabled={busy}
            aria-label={t('accessRevoked.banner.keepData')}
          >
            {t('accessRevoked.banner.keepDataShort')}
          </Button>
        )}
      </div>
      {expanded && (
        <div data-testid="banner-details" className="space-y-2 pb-2 pl-6 sm:hidden">
          <p>
            {headline} {t('accessRevoked.banner.disabledNote')}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {isDeletionOverridden ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-11 text-xs text-destructive-ink hover:bg-destructive-light"
                onClick={handleCancelOverride}
                disabled={busy}
              >
                {t('accessRevoked.banner.deleteAnyway')}
              </Button>
            ) : (
              grantAccessLink('min-h-11')
            )}
          </div>
        </div>
      )}

      <div
        data-testid="banner-desktop"
        className="mx-auto hidden max-w-screen-2xl gap-2 sm:flex sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            {headline}{' '}
            {t('accessRevoked.banner.disabledNote')}
            {isDeletionOverridden ? (
              <span className="ml-1 text-destructive-ink">{t('accessRevoked.banner.deletionPaused')}</span>
            ) : daysUntilDeletion !== null ? (
              <span className="ml-1 text-destructive-ink">
                {t('accessRevoked.banner.deletionIn', { count: daysUntilDeletion })}
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {grantAccessLink('')}
          {isDeletionOverridden ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 text-xs text-destructive-ink hover:bg-destructive-light"
              onClick={handleCancelOverride}
              disabled={busy}
            >
              {t('accessRevoked.banner.deleteAnyway')}
            </Button>
          ) : (
            <Button type="button" size="sm" className="h-7 text-xs" onClick={handleKeep} disabled={busy}>
              {t('accessRevoked.banner.keepData')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
